import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Table, Alert, ButtonGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from 'sweetalert2';
import {
  fetchBeneficios,
  createBeneficio,
  deleteBeneficio,
  updateBeneficio,
} from "../../redux/beneficiosSlice";
import DocumentMentionInput from "../../components/DocumentMentionInput";

const BeneficiosAdminPage = () => {
  const dispatch = useDispatch();
  const { beneficios, loading, error } = useSelector((state) => state.beneficios);

  const [showModal, setShowModal] = useState(false);
  const [currentBeneficio, setCurrentBeneficio] = useState({
    id: null,
    nombre: "",
    descripcion: "",
    activo: true,
    documento_referencia_id: null
  });
  const [validationErrors, setValidationErrors] = useState({
    nombre: "",
    descripcion: "",
    general: ""
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchBeneficios());
  }, [dispatch]);

  const handleUpdateBeneficio = async () => {
    setIsSaving(true);
    setValidationErrors(prev => ({ ...prev, general: "" }));

    try {
      await dispatch(updateBeneficio({
        id: currentBeneficio.id,
        beneficioData: currentBeneficio
      })).unwrap();

      dispatch(fetchBeneficios());
      handleCloseModal();
      Swal.fire({
        icon: 'success',
        title: 'Beneficio actualizado',
        text: 'El beneficio se ha actualizado correctamente.',
      });
    } catch (error) {
      console.error("Error al actualizar el beneficio:", error);
      // Manejo de errores similar a handleSaveBeneficio
      let errorMessage = 'Hubo un error al actualizar el beneficio. Por favor, inténtalo nuevamente.';

      if (error.payload) {
        if (Array.isArray(error.payload.errors)) {
          const backendErrors = {};
          error.payload.errors.forEach(err => {
            backendErrors[err.field || err.path] = err.msg || err.message;
          });
          setValidationErrors({ ...validationErrors, ...backendErrors });
          errorMessage = 'Por favor corrige los errores en el formulario.';
        } else if (error.payload.error) {
          setValidationErrors(prev => ({ ...prev, general: error.payload.error }));
          errorMessage = error.payload.error;
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };


  const handleCloseModal = () => {
    setValidationErrors({
      nombre: "",
      descripcion: "",
      general: ""
    });
    setShowModal(false);
    setCurrentBeneficio({
      id: null,
      nombre: "",
      descripcion: "",
      activo: true,
      documento_referencia_id: null
    });
  };

  const handleShowModal = (beneficio = null) => {
    setValidationErrors({
      nombre: "",
      descripcion: "",
      general: ""
    });

    if (beneficio) {
      setCurrentBeneficio({
        id: beneficio.id,
        nombre: beneficio.nombre,
        descripcion: beneficio.descripcion || "",
        activo: beneficio.activo,
        // Preserva el enlace actual si el beneficio ya tenía documento.
        documento_referencia_id: beneficio.documento_referencia_id ?? null
      });
    } else {
      setCurrentBeneficio({
        id: null,
        nombre: "",
        descripcion: "",
        activo: true,
        documento_referencia_id: null
      });
    }

    setShowModal(true);
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "nombre":
        if (!value.trim()) {
          error = "El nombre es obligatorio.";
        } else if (value.length < 2) {
          error = "El nombre debe tener al menos 2 caracteres.";
        } else if (value.length > 255) {
          error = "El nombre no debe exceder los 255 caracteres.";
        }
        break;
      case "descripcion":
        if (value && value.length < 10) {
          error = "La descripción debe tener al menos 10 caracteres.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentBeneficio({ ...currentBeneficio, [name]: value || ''});

    const error = validateField(name, value);
    setValidationErrors({ ...validationErrors, [name]: error });
  };

  // Manejo específico de la descripción vía DocumentMentionInput (recibe el texto directo).
  const handleDescripcionChange = (texto) => {
    setCurrentBeneficio((prev) => ({ ...prev, descripcion: texto || '' }));
    setValidationErrors((prev) => ({ ...prev, descripcion: validateField("descripcion", texto || '') }));
  };

  // La BD guarda UN solo documento_referencia_id: la última mención elegida gana.
  const handleDocumentosChange = (docs) => {
    setCurrentBeneficio((prev) => ({
      ...prev,
      documento_referencia_id: docs.length ? docs[docs.length - 1].id : null
    }));
  };

  const isFormValid = () => {
  return (
    !validationErrors.nombre &&
    !validationErrors.descripcion &&
    currentBeneficio.nombre &&  // Asegura que nombre existe
    currentBeneficio.nombre.trim() !== "" &&
    (currentBeneficio.descripcion === "" || 
     (currentBeneficio.descripcion && currentBeneficio.descripcion.length >= 10))
  );
};

  const handleSaveBeneficio = async () => {
    setIsSaving(true);
    setValidationErrors(prev => ({ ...prev, general: "" }));

    try {
      await dispatch(createBeneficio(currentBeneficio)).unwrap();
      dispatch(fetchBeneficios());
      handleCloseModal();
      Swal.fire({
        icon: 'success',
        title: 'Beneficio creado',
        text: 'El beneficio se ha creado correctamente.',
      });
    } catch (error) {
      console.error("Error al guardar el beneficio:", error);

      let errorMessage = 'Hubo un error al guardar el beneficio. Por favor, inténtalo nuevamente.';

      if (error.payload) {
        if (Array.isArray(error.payload.errors)) {
          const backendErrors = {};
          error.payload.errors.forEach(err => {
            backendErrors[err.field || err.path] = err.msg || err.message;
          });
          setValidationErrors({ ...validationErrors, ...backendErrors });
          errorMessage = 'Por favor corrige los errores en el formulario.';
        } else if (error.payload.error) {
          setValidationErrors(prev => ({ ...prev, general: error.payload.error }));
          errorMessage = error.payload.error;
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBeneficio = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteBeneficio(id)).unwrap();
        dispatch(fetchBeneficios());
        Swal.fire({
          icon: 'success',
          title: 'Beneficio eliminado',
          text: 'El beneficio se ha eliminado correctamente.',
        });
      } catch (error) {
        console.error("Error al eliminar el beneficio:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.payload?.error || 'Hubo un error al eliminar el beneficio.',
        });
      }
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Beneficios</h1>
        <Button
          onClick={handleShowModal}
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Crear Beneficio</span>
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message || (typeof error === 'string' ? error : 'Ocurrió un error')}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="d-block d-md-none alert alert-info mb-3">
            <i className="bi bi-phone me-1"></i>
            Desliza horizontalmente para ver todas las columnas
          </div>

          <div className="table-responsive">
            <Table striped bordered hover className="small">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th className="d-none d-md-table-cell">Descripción</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {beneficios.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">
                      No hay beneficios registrados
                    </td>
                  </tr>
                ) : (
                  beneficios.map((beneficio) => (
                    <tr key={beneficio.id}>
                      <td>{beneficio.id}</td>
                      <td title={beneficio.nombre} className="text-truncate" style={{ maxWidth: '150px' }}>
                        {beneficio.nombre}
                      </td>
                      <td
                        title={beneficio.descripcion}
                        className="d-none d-md-table-cell text-truncate"
                        style={{ maxWidth: '200px' }}
                      >
                        {beneficio.descripcion || '-'}
                      </td>
                      <td className="text-center">
                        <ButtonGroup size="sm">
                          <Button
                            variant="outline-primary"
                            onClick={() => handleShowModal(beneficio)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil"></i>
                            <span className="d-none d-lg-inline ms-1">Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDeleteBeneficio(beneficio.id)}
                            title="Eliminar"
                          >
                            <i className="bi bi-trash"></i>
                            <span className="d-none d-lg-inline ms-1">Eliminar</span>
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-plus-circle me-2"></i>
            {currentBeneficio.id ? "Editar Beneficio" : "Crear Beneficio"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationErrors.general && (
            <Alert variant="danger" dismissible onClose={() => setValidationErrors(prev => ({ ...prev, general: "" }))}>
              {validationErrors.general}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3" controlId="nombre">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                value={currentBeneficio.nombre}
                onChange={handleChange}
                isInvalid={!!validationErrors.nombre}
                placeholder="Nombre del beneficio"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.nombre}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="descripcion">
              <Form.Label>Descripción</Form.Label>
              <Form.Text className="text-muted">
  Formato: <code>**negrita**</code>, <code>*cursiva*</code>, enlaces con <code>[texto](https://url.com)</code>. Los saltos de línea se respetan.
</Form.Text>
              {/* Escribe @ para enlazar un documento existente (solo admin). */}
              <DocumentMentionInput
                value={currentBeneficio.descripcion}
                onChange={handleDescripcionChange}
                onDocumentosChange={handleDocumentosChange}
                rows={4}
                placeholder="Descripción del beneficio. Usa @ para enlazar un documento (opcional)."
              />
              {validationErrors.descripcion && (
                <div className="text-danger small mt-1">{validationErrors.descripcion}</div>
              )}
              {currentBeneficio.documento_referencia_id && (
                <div className="text-success small mt-1">
                  <i className="bi bi-link-45deg"></i> Documento enlazado (ID {currentBeneficio.documento_referencia_id})
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={currentBeneficio.id ? handleUpdateBeneficio : handleSaveBeneficio}
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {currentBeneficio.id ? "Actualizando..." : "Guardando..."}
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i>
                {currentBeneficio.id ? "Actualizar" : "Guardar"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BeneficiosAdminPage;